import { useState, useRef } from 'react';

function TaskCard({ task, onClick, onComplete, onDelete }) {
  const [isSwiping, setIsSwiping] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const currentX = useRef(0);
  const hasMoved = useRef(false);
  const cardRef = useRef(null);

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    currentX.current = startX.current;
    hasMoved.current = false;
    setIsSwiping(false);
  };

  const handleTouchMove = (e) => {
    if (!cardRef.current) return;

    currentX.current = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX.current - startX.current;
    const diffY = Math.abs(startY.current - currentY);

    if (Math.abs(diffX) > 5 || diffY > 5) {
      hasMoved.current = true;
    }

    const isHorizontalSwipe = Math.abs(diffX) > 20 && Math.abs(diffX) > diffY * 1.5;

    if (isHorizontalSwipe && Math.abs(diffX) > 20) {
      e.preventDefault();
      setIsSwiping(true);

      const swipeDistance = Math.min(Math.abs(diffX), 100);
      
      if (diffX > 0) {
        // Swipe right - complete
        cardRef.current.style.transform = `translateX(${swipeDistance}px)`;
        cardRef.current.style.transition = 'none';
        cardRef.current.style.backgroundColor = swipeDistance > 70 ? '#d1fae5' : '';
      } else {
        // Swipe left - delete
        cardRef.current.style.transform = `translateX(-${swipeDistance}px)`;
        cardRef.current.style.transition = 'none';
        cardRef.current.style.backgroundColor = swipeDistance > 70 ? '#fee' : '';
      }
    }
  };

  const handleTouchEnd = async () => {
    if (!cardRef.current) return;

    const diffX = currentX.current - startX.current;
    cardRef.current.style.transition = 'transform 0.3s ease, background-color 0.3s ease';

    if (!hasMoved.current || Math.abs(diffX) < 20) {
      cardRef.current.style.transform = '';
      cardRef.current.style.backgroundColor = '';
      setTimeout(() => setIsSwiping(false), 100);
      return;
    }

    // Swipe right > 100px - complete
    if (diffX > 100) {
      cardRef.current.style.transform = 'translateX(100%)';
      cardRef.current.style.opacity = '0';
      await new Promise(resolve => setTimeout(resolve, 300));
      onComplete(task.id);
    }
    // Swipe left > 100px - delete
    else if (diffX < -100) {
      cardRef.current.style.transform = 'translateX(-100%)';
      cardRef.current.style.opacity = '0';
      await new Promise(resolve => setTimeout(resolve, 300));
      onDelete(task.id);
    } else {
      cardRef.current.style.transform = '';
      cardRef.current.style.backgroundColor = '';
    }

    setTimeout(() => setIsSwiping(false), 100);
  };

  const handleClick = () => {
    if (!isSwiping) {
      onClick();
    }
  };

  const priorityColors = {
    high: 'border-l-4 border-red-500',
    medium: 'border-l-4 border-yellow-500',
    low: 'border-l-4 border-blue-500',
  };

  // Calculate checklist progress
  const completedCount = task.checklist?.filter(c => c.isCompleted).length || 0;
  const totalCount = task.checklist?.length || 0;
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Status icon
  const statusIcon = task.status === 'inprocess' ? '🔄 ' : '';

  return (
    <div
      ref={cardRef}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`bg-white rounded-lg shadow-sm p-3 cursor-pointer hover:shadow-md transition-shadow ${
        task.priority ? priorityColors[task.priority] : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 text-xs leading-tight mb-1 truncate">
            {statusIcon}{task.text}
          </h3>
          
          {task.checklist?.length > 0 && (
            <div className="mt-1.5 space-y-1">
              {/* Progress bar */}
              {totalCount > 0 && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-1">
                    <div
                      className="bg-gradient-to-r from-violet-500 to-purple-500 h-1 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 font-medium min-w-[2.5rem]">
                    {completedCount}/{totalCount}
                  </span>
                </div>
              )}
              
              {/* Checklist items preview - только первые 2 элемента */}
              <div className="space-y-0.5">
                {task.checklist.slice(0, 1).map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1 text-xs text-gray-600">
                    <div className={`w-2.5 h-2.5 rounded border flex items-center justify-center ${
                      item.isCompleted ? 'bg-green-500 border-green-500' : 'border-gray-300'
                    }`}>
                      {item.isCompleted && (
                        <svg className="w-1.5 h-1.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                        </svg>
                      )}
                    </div>
                    <span className={`truncate max-w-[200px] ${item.isCompleted ? 'line-through text-gray-400' : ''}`}>
                      {item.text || item}
                    </span>
                  </div>
                ))}
                {task.checklist.length > 1 && (
                  <p className="text-xs text-gray-400 ml-3.5">+{task.checklist.length - 1} еще</p>
                )}
              </div>
            </div>
          )}

          {/* Tags - ограничиваем количество для компактности */}
          {task.tags?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-0.5">
              {task.tags.slice(0, 2).map((tag, idx) => (
                <span key={idx} className="text-xs px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 truncate max-w-[60px]">
                  #{tag}
                </span>
              ))}
              {task.tags.length > 2 && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                  +{task.tags.length - 2}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          {task.priority && (
            <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
              {task.priority === 'high' ? '🔴' : task.priority === 'medium' ? '🟡' : '🔵'}
            </span>
          )}
          
        </div>
      </div>
    </div>
  );
}

export default TaskCard;


